<script module lang="ts">
  type ChatRole = 'agent' | 'user' | 'system'

  export type ChatMessage = {
    id: string
    role: ChatRole
    text: string
    author?: string
    avatarUrl?: string
    timestamp?: string
    meta?: Record<string, unknown>
    maxMessagesReached?: boolean
  }
</script>

<script lang="ts">
  import { createEventDispatcher, onMount, tick } from 'svelte'
  import { fade, fly, scale } from 'svelte/transition'
  import type { WidgetBranding, WidgetFeatures } from './types.js'
  import {
    buildThemeStyles,
    deriveThemeFromBranding,
    normalizeHex,
  } from './theme.js'

  // Default welcome message (fallback only, runtime config overrides this)
  const defaultWelcomeAuthor = 'Sky from Support'
  const defaultWelcomeText =
    "Hey there! I'm here if you have any questions about the product."

  let {
    title = 'Need a hand?',
    subtitle = 'Our team typically replies in under 5 minutes.',
    accentColor = '#6366f1',
    backgroundColor = '#0f172a',
    surfaceColor = '#111c36',
    open = $bindable(false),
    placeholder = 'Send us a message...',
    inputAutoFocus = false,
    typing = false,
    quickReplies = [],
    maxMessages = 15,
    maxUserCharacters = 500,
    branding = null,
    features = {
      enableFileUpload: false,
      enableRating: true,
      enableTranscript: true,
      showPoweredBy: true,
    },
    customCss = '',
    messages = $bindable<ChatMessage[]>([
      {
        id: 'agent-welcome',
        role: 'agent',
        author: defaultWelcomeAuthor,
        text: defaultWelcomeText,
        timestamp: new Date().toISOString(),
      },
    ]),
  }: {
    title?: string
    subtitle?: string
    accentColor?: string
    backgroundColor?: string
    surfaceColor?: string
    open?: boolean
    placeholder?: string
    inputAutoFocus?: boolean
    typing?: boolean
    quickReplies?: string[]
    maxMessages?: number
    maxUserCharacters?: number
    branding?: WidgetBranding | null
    features?: WidgetFeatures
    customCss?: string
    messages?: ChatMessage[]
  } = $props()

  const dispatch = createEventDispatcher<{
    toggle: { open: boolean }
    send: { message: ChatMessage; messages: ChatMessage[] }
    'quick-reply': { text: string }
    'message-blocked': { reason: 'length' | 'topic'; text: string }
  }>()

  let draft = $state('')
  let container = $state<HTMLDivElement | null>(null)
  let composer = $state<HTMLTextAreaElement | null>(null)
  let quickRepliesUsed = $state(false)
  let composerError = $state<string | null>(null)

  const timeFormatter = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })

  let themeStyles = $state('')
  let isMobileViewport = $state(false)
  let viewportHeight = $state<number | null>(null)
  let viewportOffsetTop = $state(0)
  let widgetStyles = $state('')

  $effect(() => {
    const baseStyles = buildThemeStyles({
      accent: safeAccent(accentColor),
      secondary: branding?.secondaryColor,
      background: branding?.backgroundColor || backgroundColor,
      surface: branding?.surfaceColor || surfaceColor,
      palette: branding?.palette,
    })
    const font = branding?.fontFamily?.trim()
    themeStyles =
      font && font.length > 0
        ? `${baseStyles}\n--chat-font-family:${font};`
        : baseStyles
  })

  $effect(() => {
    const viewportChunk =
      viewportHeight !== null
        ? `\n--chat-viewport-height:${viewportHeight}px;\n--chat-viewport-offset:${viewportOffsetTop}px;`
        : ''
    widgetStyles = `${themeStyles}${viewportChunk}`
  })

  onMount(() => {
    if (open && inputAutoFocus) {
      focusComposer()
    }
  })

  onMount(() => {
    if (typeof window === 'undefined') return
    const mediaQuery = window.matchMedia('(max-width: 640px)')
    const handleChange = (event: MediaQueryListEvent) => {
      isMobileViewport = event.matches
    }
    isMobileViewport = mediaQuery.matches
    mediaQuery.addEventListener('change', handleChange)
    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  })

  function updateViewportMetrics() {
    if (typeof window === 'undefined') return
    if (!isMobileViewport || !open) return

    const visualViewport = window.visualViewport
    const nextHeight = visualViewport?.height ?? window.innerHeight
    const nextOffset = visualViewport?.offsetTop ?? 0

    if (typeof nextHeight === 'number' && nextHeight > 0) {
      viewportHeight = Math.round(nextHeight)
    }

    viewportOffsetTop =
      typeof nextOffset === 'number' && nextOffset > 0
        ? Math.round(nextOffset)
        : 0
  }

  onMount(() => {
    if (typeof window === 'undefined') return
    const handleViewportChange = () => {
      updateViewportMetrics()
      if (isMobileViewport && open) {
        scrollToBottom(true)
      }
    }

    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('orientationchange', handleViewportChange)

    const visualViewport = window.visualViewport
    visualViewport?.addEventListener('resize', handleViewportChange)
    visualViewport?.addEventListener('scroll', handleViewportChange)

    return () => {
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('orientationchange', handleViewportChange)
      visualViewport?.removeEventListener('resize', handleViewportChange)
      visualViewport?.removeEventListener('scroll', handleViewportChange)
    }
  })

  $effect(() => {
    if (isMobileViewport && open) {
      updateViewportMetrics()
    } else {
      viewportHeight = null
      viewportOffsetTop = 0
    }
  })

  $effect(() => {
    if (open) {
      scrollToBottom()
    }
  })

  // Auto-scroll when new messages are added
  $effect(() => {
    if (open && messages.length > 0) {
      // Use a small delay to ensure DOM has updated
      setTimeout(() => {
        scrollToBottom(true)
      }, 50)
    }
  })

  $effect(() => {
    if (messages.length > maxMessages) {
      messages = enforceMessageLimit(messages)
    }
  })

  $effect(() => {
    if (typeof document === 'undefined') return
    const className = 'chat-widget-scroll-lock'
    if (open && isMobileViewport) {
      document.body.classList.add(className)
      return () => {
        document.body.classList.remove(className)
      }
    } else {
      document.body.classList.remove(className)
    }
  })

  // Apply tenant branding
  $effect(() => {
    const rootStyle = document.documentElement.style

    const variables = deriveThemeFromBranding(
      branding,
      safeAccent(accentColor),
      branding?.backgroundColor || backgroundColor,
      branding?.surfaceColor || surfaceColor
    )

    for (const [key, value] of Object.entries(variables)) {
      rootStyle.setProperty(key, value)
    }
    rootStyle.setProperty(
      '--chat-font-family',
      branding?.fontFamily?.trim() || 'inherit'
    )

    if (branding?.customCss || customCss) {
      const styleElement = document.createElement('style')
      styleElement.textContent = branding?.customCss || customCss
      styleElement.id = 'tenant-custom-css'

      const existing = document.getElementById('tenant-custom-css')
      if (existing) existing.remove()

      document.head.appendChild(styleElement)
    } else {
      const existing = document.getElementById('tenant-custom-css')
      if (existing) existing.remove()
    }
  })

  function enforceMessageLimit(list: ChatMessage[]) {
    if (!maxMessages || maxMessages < 1) return list
    return list.slice(Math.max(list.length - maxMessages, 0))
  }

  function setComposerError(message: string) {
    composerError = message
    setTimeout(() => {
      if (composerError === message) {
        composerError = null
      }
    }, 4000)
  }

  function safeAccent(color: string) {
    return normalizeHex(color, '#6366f1')
  }

  function toggle() {
    open = !open
    dispatch('toggle', { open })
    if (open) {
      focusComposer()
    }
  }

  async function sendMessage() {
    const trimmed = draft.trim()
    if (!trimmed) return

    if (trimmed.length > maxUserCharacters) {
      setComposerError(`Message limit is ${maxUserCharacters} characters.`)
      dispatch('message-blocked', { reason: 'length', text: trimmed })
      return
    }

    const nextMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      text: trimmed,
      timestamp: new Date().toISOString(),
    }

    messages = enforceMessageLimit([...messages, nextMessage])
    dispatch('send', { message: nextMessage, messages })
    draft = ''
    composerError = null
    await tick()
    scrollToBottom(true)
  }

  async function scrollToBottom(smooth = false) {
    await tick()
    if (!container) return

    // container is already bound to the .messages div, so we can scroll it directly
    container.scrollTo({
      top: container.scrollHeight,
      behavior: smooth ? 'smooth' : 'auto',
    })
  }

  function focusComposer() {
    setTimeout(() => {
      composer?.focus()
    }, 60)
  }

  function formattedTime(timestamp?: string) {
    if (!timestamp) return ''
    try {
      return timeFormatter.format(new Date(timestamp))
    } catch {
      return ''
    }
  }

  function useQuickReply(text: string) {
    draft = text
    // quickRepliesUsed = true; // Hide quick replies after selection
    dispatch('quick-reply', { text })
    sendMessage()
  }

  function parseMarkdown(text: string): string {
    if (typeof text !== 'string') return ''

    let html = text
      // Handle line breaks first
      .replace(/\n/g, '<br>')
      // Bold text: **text** or __text__
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      // Italic text: *text* or _text_
      .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>')
      .replace(/(?<!_)_([^_]+)_(?!_)/g, '<em>$1</em>')
      // Code: `text`
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      // Markdown links: [text](url)
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, url) =>
        createAnchor(url, label)
      )

    const protectedSegments: string[] = []
    const placeholder = (segment: string) => {
      const token = `__MD_PLACEHOLDER_${protectedSegments.length}__`
      protectedSegments.push(segment)
      return token
    }

    // Prevent auto-linking inside existing anchors or inline code
    html = html
      .replace(/<code>.*?<\/code>/g, (segment) => placeholder(segment))
      .replace(/<a\b[^>]*>.*?<\/a>/g, (segment) => placeholder(segment))

    html = linkifyBareUrls(html)

    return html.replace(/__MD_PLACEHOLDER_(\d+)__/g, (_, index) => {
      const idx = Number(index)
      return Number.isNaN(idx) ? '' : (protectedSegments[idx] ?? '')
    })
  }

  function createAnchor(url: string, label: string) {
    const safeHref = sanitizeUrl(url)
    if (!safeHref) {
      return label
    }
    return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${label}</a>`
  }

  function sanitizeUrl(raw: string): string | null {
    if (typeof raw !== 'string') return null
    const trimmed = raw.trim()
    if (!/^https?:\/\//i.test(trimmed)) return null
    return trimmed.replace(/"/g, '&quot;')
  }

  function linkifyBareUrls(input: string): string {
    return input.replace(
      /(^|[^\w"'@])((?:https?:\/\/)[^\s<]+)/gi,
      (match, prefix, url) => {
        const anchor = createAnchor(url, url)
        if (anchor === url) {
          return match
        }
        return `${prefix}${anchor}`
      }
    )
  }

  function isImageSource(value?: string): boolean {
    if (typeof value !== 'string') return false
    const trimmed = value.trim()
    if (!trimmed) return false
    return /^https?:\/\//i.test(trimmed) || trimmed.startsWith('data:image/')
  }
</script>

<div
  class="chat-widget"
  style={widgetStyles}
  data-open={open}
  data-mobile={isMobileViewport}
  data-fullscreen={isMobileViewport && open}
>
  <button
    class="launcher"
    aria-expanded={open}
    onclick={toggle}
    in:scale={{ start: 0.8, duration: 150 }}
    out:scale={{ duration: 120 }}
  >
    <span class="launcher-icon" aria-hidden="true">
      {#if open}
        {#if branding?.launcherIconOpen}
          {#if isImageSource(branding.launcherIconOpen)}
            <img
              src={branding.launcherIconOpen}
              alt=""
              class="launcher-icon-image"
            />
          {:else}
            <span class="launcher-icon-text">{branding.launcherIconOpen}</span>
          {/if}
        {:else}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M6 6l12 12M6 18L18 6"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        {/if}
      {:else if branding?.launcherIcon}
        {#if branding?.launcherIcon === 'default'}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M4 4h16v11.5a1.5 1.5 0 0 1-1.5 1.5H9l-5 3v-16A1.5 1.5 0 0 1 5.5 4H4z"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <circle cx="9" cy="10" r="1.2" fill="currentColor" />
            <circle cx="15" cy="10" r="1.2" fill="currentColor" />
          </svg>
          {#if isImageSource(branding.launcherIcon)}
            <img
              src={branding.launcherIcon}
              alt=""
              class="launcher-icon-image"
            />
          {/if}
        {:else}
          <span class="launcher-icon-text">{branding.launcherIcon}</span>
        {/if}
      {/if}
    </span>
  </button>

  {#if open}
    <section
      class="panel"
      in:fly={{ y: 16, duration: 180, easing: (t) => t * (2 - t) }}
      out:fade={{ duration: 120 }}
    >
      <header class="header">
        <div class="header-content">
          <div class="header-icon">
            {#if branding?.logo}
              <img
                src={branding.logo}
                alt={branding.name}
                class="tenant-logo"
              />
            {:else if branding?.headerIcon}
              {#if isImageSource(branding.headerIcon)}
                <img
                  src={branding.headerIcon}
                  alt={branding.name}
                  class="tenant-logo"
                />
              {:else}
                <span aria-hidden="true">{branding.headerIcon}</span>
              {/if}
            {:else}
              <span aria-hidden="true">💬</span>
            {/if}
          </div>
          <div class="header-text">
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
          {#if isMobileViewport}
            <button
              type="button"
              class="header-close"
              aria-label="Close chat"
              onclick={toggle}
            >
              ✕
            </button>
          {/if}
        </div>
      </header>

      <div class="body">
        <div class="messages" bind:this={container}>
          {#each messages as message (message.id)}
            <article class={`message message-${message.role}`}>
              {#if message.role === 'agent' && message.author}
                <span class="meta">
                  {message.author}
                  {#if formattedTime(message.timestamp)}
                    <span> · {formattedTime(message.timestamp)}</span>
                  {/if}
                </span>
              {/if}
              <div class="bubble">
                {@html parseMarkdown(message.text)}
              </div>
              {#if message.role !== 'agent' && formattedTime(message.timestamp)}
                <span class="meta meta-inline"
                  >{formattedTime(message.timestamp)}</span
                >
              {/if}
            </article>
          {/each}

          {#if typing}
            <div class="message message-agent typing-indicator">
              <div class="bubble">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          {/if}
        </div>

        {#if quickReplies.length && !quickRepliesUsed}
          <div class="quick-replies">
            {#each quickReplies as reply}
              <button type="button" onclick={() => useQuickReply(reply)}>
                {reply}
              </button>
            {/each}
          </div>
        {/if}
      </div>

      <form
        class="composer"
        onsubmit={(e) => {
          e.preventDefault()
          sendMessage()
        }}
        autocomplete="off"
        spellcheck="true"
      >
        <textarea
          bind:this={composer}
          bind:value={draft}
          {placeholder}
          class="composer-input"
          rows="1"
          onkeydown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              sendMessage()
            }
          }}
        ></textarea>
        <button class="composer-send" type="submit" aria-label="Send message">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M4.5 12H11m0 0l-3-2m3 2l-3 2m9 6L4 12 18 6l-1 14z"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        {#if composerError}
          <div class="composer-error" role="status">{composerError}</div>
        {/if}
      </form>

      <!-- Conditional powered by branding -->
      {#if features.showPoweredBy !== false}
        <div class="powered-by">Powered by Webtek.lv</div>
      {/if}
    </section>
  {/if}
</div>

<style>
  :global(body) {
    font-family:
      'Inter',
      system-ui,
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      sans-serif;
  }

  :global(body.chat-widget-scroll-lock) {
    overflow: hidden;
    touch-action: none;
  }

  /* 
   * CSS Custom Properties for easy customization
   * 
   * To customize colors, override these variables in your CSS:
   * 
   * .chat-widget {
   *   --chat-primary: #your-primary-color;
   *   --chat-primary-light: #your-light-primary;
   *   --chat-bg-primary: #your-background;
   *   --chat-bg-secondary: #your-surface-color;
   *   --chat-text-primary: #your-text-color;
   *   --chat-text-secondary: #your-secondary-text;
   * }
   * 
   * Or set them via CSS custom properties in your app's root:
   * :root {
   *   --chat-primary: #6366f1;
   *   --chat-bg-primary: #0f172a;
   *   --chat-text-primary: #f8fafc;
   * }
   */
  .chat-widget {
    /* Primary Colors */
    --chat-primary: #7c23cf;
    --chat-primary-hover: #204dd8;
    --chat-primary-light: #204dd8;

    /* Background Colors */
    --chat-bg-primary: #0d1a36;
    --chat-bg-secondary: #14254a;
    --chat-bg-surface: rgba(13, 26, 54, 0.72);
    --chat-bg-surface-hover: rgba(13, 26, 54, 0.82);

    /* Text Colors */
    --chat-text-primary: #f4f6ff;
    --chat-text-secondary: #dce3ff;
    --chat-text-muted: rgba(226, 230, 255, 0.55);

    /* Border Colors */
    --chat-border: rgba(184, 195, 225, 0.16);
    --chat-border-light: rgba(184, 195, 225, 0.1);

    /* Shadow Colors */
    --chat-shadow: rgba(13, 26, 54, 0.45);
    --chat-shadow-light: rgba(13, 26, 54, 0.25);
    --chat-link-color: #204dd8;
    --chat-link-hover-color: #7c23cf;
  }

  .chat-widget {
    position: fixed;
    bottom: 1.75rem;
    right: 1.75rem;
    display: grid;
    gap: 0.75rem;
    z-index: 50;
    color: var(--chat-text-primary);
    font-family: inherit;
    width: max-content;
    --shadow-elevated: 0 20px 45px var(--chat-shadow),
      0 0 1px var(--chat-border);
  }

  .chat-widget[data-mobile='true'] {
    right: 1rem;
    bottom: 1rem;
  }

  @supports (bottom: env(safe-area-inset-bottom)) {
    .chat-widget[data-mobile='true'] {
      right: calc(env(safe-area-inset-right) + 0.5rem);
      bottom: calc(env(safe-area-inset-bottom) + 0.5rem);
    }
  }

  .chat-widget[data-fullscreen='true'] {
    left: 0;
    right: 0;
    top: var(--chat-viewport-offset, 0px);
    bottom: auto;
    width: 100%;
    height: var(--chat-viewport-height, 100vh);
    max-height: 100vh;
    gap: 0;
    justify-items: stretch;
    align-content: stretch;
    background: var(--chat-bg-primary);
  }

  .chat-widget[data-fullscreen='true'] .launcher {
    display: none;
  }

  .launcher {
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 999px;
    border: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--chat-launcher-bg);
    color: var(--chat-launcher-icon);
    cursor: pointer;
    box-shadow: var(--shadow-elevated);
    transition:
      transform 0.2s ease,
      box-shadow 0.2s ease,
      filter 0.2s ease;
  }

  .launcher:hover {
    transform: translateY(-1px) scale(1.02);
    filter: brightness(1.05);
  }

  .launcher:active {
    transform: scale(0.97);
  }

  .launcher-icon {
    width: 1.55rem;
    height: 1.55rem;
    display: inline-flex;
  }

  .launcher-icon svg {
    width: 100%;
    height: 100%;
    stroke: currentColor;
  }

  .launcher-icon-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .launcher-icon-text {
    display: inline-flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
    font-size: 1.35rem;
    line-height: 1;
  }

  .panel {
    width: min(360px, calc(100vw - 3rem));
    backdrop-filter: blur(22px);
    background: linear-gradient(
        155deg,
        rgba(15, 23, 42, 0.75) 0%,
        rgba(17, 24, 39, 0.9) 100%
      ),
      var(--chat-background);
    border-radius: 1.25rem;
    border: 1px solid rgba(148, 163, 184, 0.1);
    box-shadow: var(--shadow-elevated);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .chat-widget[data-fullscreen='true'] .panel {
    width: 100%;
    max-width: none;
    height: 100%;
    min-height: 0;
    border-radius: 0;
    border: 0;
    box-shadow: none;
  }

  .header {
    padding: 1.25rem 1.5rem;
    background: linear-gradient(
      140deg,
      var(--chat-gradient-start) 0%,
      var(--chat-gradient-end) 100%
    );
  }

  .chat-widget[data-fullscreen='true'] .header {
    padding-top: calc(1.25rem + env(safe-area-inset-top));
  }

  .header-content {
    display: flex;
    align-items: center;
    gap: 0.85rem;
  }

  .header-close {
    margin-left: auto;
    width: 2.2rem;
    height: 2.2rem;
    border-radius: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(15, 23, 42, 0.18);
    color: inherit;
    font-size: 1rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  @media (min-width: 641px) {
    .header-close {
      display: none;
    }
  }

  .header-icon {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.9rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.16);
    font-size: 1.35rem;
  }

  .header-text h2 {
    font-size: 1.08rem;
    font-weight: 600;
    margin: 0;
    color: var(--chat-header-title);
  }

  .header-text p {
    margin: 0.15rem 0 0;
    font-size: 0.86rem;
    color: var(--chat-header-subtitle);
    opacity: 0.85;
  }

  .tenant-logo {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.9rem;
    object-fit: cover;
    border: 2px solid rgba(255, 255, 255, 0.2);
  }

  .body {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: var(--chat-bg-secondary);
    min-height: 0;
  }

  .chat-widget[data-fullscreen='true'] .body {
    max-height: none;
    min-height: 0;
  }

  .messages {
    flex: 1;
    padding: 1.25rem;
    overflow-y: auto;
    display: grid;
    gap: 0.9rem;
    max-height: min(80vh, 420px);
    align-content: start;
    justify-items: stretch;
    background: radial-gradient(
        circle at top,
        var(--chat-primary-light),
        transparent 45%
      ),
      linear-gradient(
        180deg,
        var(--chat-bg-surface) 0%,
        var(--chat-bg-surface-hover) 100%
      );
    min-height: 0;
  }

  .chat-widget[data-fullscreen='true'] .messages {
    max-height: none;
    align-content: start;
  }

  .messages::-webkit-scrollbar {
    width: 6px;
  }

  .messages::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.35);
    border-radius: 999px;
  }

  .message {
    display: grid;
    gap: 0.35rem;
    max-width: 92%;
  }

  .message-user {
    justify-self: end;
    text-align: right;
  }

  .message-agent {
    justify-self: start;
  }

  .message-system {
    justify-self: center;
    opacity: 0.8;
  }

  .message .bubble {
    padding: 0.8rem 1rem;
    border-radius: 1rem;
    font-size: 0.92rem;
    line-height: 1.4;
    background: var(--chat-bg-surface);
    border: 1px solid var(--chat-border-light);
    box-shadow: 0 12px 24px var(--chat-shadow-light);
    backdrop-filter: blur(14px);
  }

  .message .bubble :global(strong) {
    font-weight: 600;
    color: inherit;
  }

  .message .bubble :global(em) {
    font-style: italic;
    color: inherit;
  }

  .message .bubble :global(code) {
    background: rgba(148, 163, 184, 0.15);
    padding: 0.15rem 0.35rem;
    border-radius: 0.35rem;
    font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas,
      'Courier New', monospace;
    font-size: 0.85em;
    color: inherit;
  }

  .message .bubble :global(a) {
    color: var(--chat-link-color);
    text-decoration: underline;
    text-decoration-color: var(--chat-link-color);
    text-underline-offset: 0.15em;
    transition:
      color 0.15s ease,
      text-decoration-color 0.15s ease;
  }

  .message .bubble :global(a:hover) {
    color: var(--chat-link-hover-color);
    text-decoration-color: var(--chat-link-hover-color);
  }

  .message-user .bubble {
    background: var(--chat-user-message-bg);
    color: var(--chat-user-message-text);
    border: 1px solid transparent;
    box-shadow: 0 10px 22px var(--chat-user-message-shadow);
  }

  .message-agent .bubble {
    background: var(--chat-agent-message-bg);
    color: var(--chat-agent-message-text);
    border: 1px solid var(--chat-primary-light);
  }

  .message-system .bubble {
    background: var(--chat-border-light);
    border-color: transparent;
    color: var(--chat-text-secondary);
    opacity: 0.7;
    font-size: 0.82rem;
  }

  .message .meta {
    font-size: 0.75rem;
    color: var(--chat-text-muted);
  }

  .message .meta-inline {
    display: inline-block;
    margin-top: 0.25rem;
  }

  .typing-indicator .bubble {
    display: inline-flex;
    gap: 0.45rem;
    align-items: center;
    width: auto;
  }

  .typing-indicator span {
    width: 0.45rem;
    height: 0.45rem;
    border-radius: 50%;
    background: var(--chat-accent-soft);
    animation: typing-bounce 1.2s infinite ease-in-out;
  }

  .typing-indicator span:nth-child(2) {
    animation-delay: 0.12s;
  }

  .typing-indicator span:nth-child(3) {
    animation-delay: 0.24s;
  }

  .quick-replies {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.5rem 0.25rem 0.75rem;
    background: var(--chat-bg-surface);
  }

  .quick-replies button {
    border-radius: 999px;
    padding: 0.38rem 0.95rem;
    font-size: 0.8rem;
    font-weight: 500;
    background: var(--chat-bg-surface);
    color: var(--chat-text-secondary);
    border: 1px solid var(--chat-border-light);
    cursor: pointer;
    transition:
      background 0.18s ease,
      color 0.18s ease,
      border-color 0.18s ease,
      box-shadow 0.18s ease,
      transform 0.18s ease;
  }

  .quick-replies button:hover {
    background: var(--chat-accent-soft);
    border-color: var(--chat-border);
    color: var(--chat-text-primary);
    transform: translateY(-1px);
    box-shadow: 0 8px 20px var(--chat-shadow-light);
  }

  .quick-replies button:focus-visible {
    outline: none;
    border-color: var(--chat-primary);
    box-shadow:
      0 0 0 2px var(--chat-primary-light),
      0 10px 24px var(--chat-shadow-light);
    color: var(--chat-text-primary);
    background: var(--chat-accent-soft);
  }

  .quick-replies button:active {
    transform: translateY(0);
  }

  .composer {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0.75rem;
    padding: 1.05rem 1.25rem 1.25rem;
    background: var(--chat-bg-surface-hover);
    border-top: 1px solid var(--chat-border);
  }

  .chat-widget[data-fullscreen='true'] .composer {
    padding-bottom: calc(1.25rem + env(safe-area-inset-bottom));
  }

  .composer-input {
    resize: none;
    border-radius: 0.9rem;
    border: 1px solid var(--chat-primary-light);
    background: var(--chat-bg-surface);
    color: var(--chat-text-secondary);
    font-size: 0.92rem;
    padding: 0.75rem 0.95rem;
    line-height: 1.3;
    transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease;
    min-height: 2.6rem;
  }

  .composer-input:focus {
    outline: none;
    border-color: var(--chat-primary);
    box-shadow: 0 0 0 3px var(--chat-primary-light);
  }

  .composer-send {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.85rem;
    border: 0;
    background: var(--chat-send-button-bg);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--chat-send-button-icon);
    cursor: pointer;
    transition:
      transform 0.18s ease,
      box-shadow 0.18s ease;
    box-shadow: 0 10px 18px var(--chat-user-message-shadow);
  }

  .composer-send:hover {
    transform: translateY(-1px) scale(1.02);
  }

  .composer-send svg {
    width: 1.35rem;
    height: 1.35rem;
  }

  .composer-error {
    grid-column: 1 / -1;
    align-self: center;
    font-size: 0.78rem;
    line-height: 1.2;
    padding: 0.35rem 0.75rem;
    border-radius: 0.6rem;
    background: rgba(125, 35, 207, 0.12);
    color: var(--chat-text-secondary);
    border: 1px solid rgba(109, 213, 255, 0.25);
  }

  @keyframes typing-bounce {
    0%,
    80%,
    100% {
      transform: translateY(0);
      opacity: 0.6;
    }
    40% {
      transform: translateY(-4px);
      opacity: 1;
    }
  }

  .powered-by {
    text-align: center;
    font-size: 0.75rem;
    color: var(--chat-text-secondary);
    padding: 0.75rem 1rem;
    border-top: 1px solid var(--chat-border);
    background: var(--chat-bg-surface);
    opacity: 0.92;
  }

  /* CSS custom properties for easy tenant theming */
  :global(:root) {
    --chat-font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
      sans-serif;
  }

  :global(*) {
    font-family: var(--chat-font-family);
  }

  @media (max-width: 720px) {
    .chat-widget {
      bottom: 1.25rem;
      right: 1.25rem;
      /* left: 1.25rem; */
    }

    .panel {
      width: 100%;
      margin: 0 auto;
      border-radius: 1.1rem;
    }

    .messages {
      padding: 1.05rem;
    }
  }
</style>
